import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useShareApplicants, useLinks } from "hooks";
import { formatCurrency, getUrlFromLinks } from "utils";
import { colors, fontSize } from "style";
import { EXTERNAL_LINK_VALUES, RATE_FREQUENCIES } from "constants/index";
import { Spinner } from "components/Loader";
import { MessagesError } from "components/Messages";
import { UserLocation } from "components/User";
import { PrimaryLink } from "components/Links";

/**
 * ApplicantsShareRichText
 *
 * @param {Array} applicantIds
 */
const ApplicantsShareRichText = ({ applicantIds }) => {
  const links = useLinks();
  const { data, loading, error } = useShareApplicants({ applicantIds });

  if (loading) return <Spinner size="1.5rem" />;
  if (error) return <MessagesError />;

  return (
    <div css={styles.container}>
      {data.map((applicant) => (
        <div key={applicant.id} css={styles.block}>
          {/* Name + LinkedIn + Rate */}
          <div>
            <PrimaryLink to={links.applicant_preview(applicant.uuid)} target="_blank">
              {applicant.profile.name}
            </PrimaryLink>

            {getUrlFromLinks(applicant.profile.externalLinks, EXTERNAL_LINK_VALUES.LinkedIn) && (
              <PrimaryLink
                to={getUrlFromLinks(applicant.profile.externalLinks, EXTERNAL_LINK_VALUES.LinkedIn)}
                target="_blank"
                css={styles.linkedin}
              >
                <FormattedMessage id="Missions.Applicants.ApplicantsShareRichText.Linkedin" />
              </PrimaryLink>
            )}

            {applicant.negotiatedSellingRate?.amount && (
              <>
                <FormattedMessage id="Global.EmDash" />
                <span css={styles.negotiatedRate}>
                  <FormattedMessage
                    id="Profile.PreferredRateTag.Rate"
                    values={{
                      rate: formatCurrency(
                        applicant.negotiatedSellingRate.amount,
                        applicant.negotiatedSellingRate.currency
                      ),
                      frequency: (
                        <FormattedMessage
                          id={`Global.${
                            applicant.negotiatedSellingRate.frequency === RATE_FREQUENCIES.hourly
                              ? "PerHour"
                              : "PerMonth"
                          }`}
                        />
                      ),
                    }}
                  />
                </span>
              </>
            )}
          </div>

          {/* PIN */}
          {applicant.missionRole.mission.organization.applicationsPin && (
            <div css={styles.pin}>
              <FormattedMessage
                id="Missions.Applicants.ApplicantsShareRichText.PIN"
                values={{ pin: applicant.missionRole.mission.organization.applicationsPin }}
              />
            </div>
          )}

          {/* Location */}
          <UserLocation data={applicant.profile} />

          {/* Role */}
          {(applicant.missionRole.name || applicant.missionRole.mission.name) && (
            <div>
              <FormattedMessage
                id="Missions.Applicants.ApplicantsShareRichText.AppliedTo"
                values={{
                  role: applicant.missionRole.name,
                  mission: applicant.missionRole.mission.name,
                  bold: (chunks) => <span css={styles.bold}>{chunks}</span>,
                }}
              />
            </div>
          )}

          {/* Disciplines */}
          {applicant.profile.disciplines?.length > 0 && (
            <div>
              <FormattedMessage
                id="Missions.Applicants.ApplicantsShareRichText.Disciplines"
                values={{
                  disciplines: applicant.profile.disciplines.map(({ name }) => name).join(", "),
                  bold: (chunks) => <span css={styles.bold}>{chunks}</span>,
                }}
              />
            </div>
          )}

          {/* Skills */}
          {applicant.profile.skills?.length > 0 && (
            <div>
              <FormattedMessage
                id="Missions.Applicants.ApplicantsShareRichText.Skills"
                values={{
                  skills: applicant.profile.skills.map(({ name }) => name).join(", "),
                  bold: (chunks) => <span css={styles.bold}>{chunks}</span>,
                }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: css`
    padding: 1.4rem;
    background: ${colors.grayAnatomyLight5};
    border-radius: 0.6rem;
    height: 500px;
    overflow-y: auto;
  `,
  block: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyDark};
    line-height: 1.7rem;
    margin-bottom: 1.8rem;
    border-bottom: 1px dashed ${colors.grayAnatomyLight3};
    padding-bottom: 1.2rem;
  `,
  linkedin: css`
    margin-left: 0.5rem;
    font-weight: 500;
  `,
  negotiatedRate: css`
    font-weight: 500;
  `,
  pin: css`
    font-weight: 600;
    color: ${colors.grayAnatomyDark};
    margin-top: 0.2rem;
  `,
  bold: css`
    font-weight: 600;
  `,
};

ApplicantsShareRichText.propTypes = {
  applicantIds: PropTypes.array.isRequired,
};

export default ApplicantsShareRichText;
